import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { X, Check, ListTodo, Target } from 'lucide-react';

export function ProblemSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 sm:py-32 relative">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Drowning in To-Dos?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Most apps make you a librarian of your tasks. Luma makes you an <span className="text-foreground font-medium">executor</span>.
          </p>
        </motion.div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {/* Other Apps - The Problem */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-destructive/5 blur-2xl rounded-3xl opacity-50" />
            <div className="relative bg-card/50 border border-border/50 rounded-2xl p-6 sm:p-8 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <X className="h-5 w-5 text-destructive" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Other Apps</h3>
                  <p className="text-sm text-muted-foreground">Task hoarding</p>
                </div>
              </div>

              <div className="space-y-3 opacity-60">
                {[
                  'Buy groceries (someday)',
                  'Call dentist',
                  'Q4 planning review',
                  'Learn guitar',
                  'Finish presentation',
                  'Reply to Mike',
                  'Clean garage',
                  '... 47 more tasks',
                ].map((task, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg"
                  >
                    <ListTodo className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground line-clamp-1">{task}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                <p className="text-sm text-destructive font-medium text-center">
                  Everything feels urgent. Nothing gets done.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Luma - The Solution */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="relative group"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 to-primary/10 blur-2xl rounded-3xl" />
            <div className="relative bg-card border border-primary/30 rounded-2xl p-6 sm:p-8 shadow-lg">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Luma</h3>
                  <p className="text-sm text-muted-foreground">Focused execution</p>
                </div>
              </div>

              {/* The ONE Thing */}
              <div className="mb-6">
                <div className="relative">
                  <div className="absolute -inset-2 bg-primary/20 blur-xl rounded-2xl" />
                  <div className="relative bg-background border border-primary/50 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-4 w-4 text-primary" />
                      <span className="text-xs text-primary font-semibold uppercase tracking-wide">
                        The ONE Thing
                      </span>
                    </div>
                    <p className="font-medium">Finish presentation deck</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="px-2 py-0.5 bg-primary/20 rounded text-xs text-primary">
                        Deep Work
                      </div>
                      <span className="text-xs text-muted-foreground">2h focused</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Other tasks - dimmed */}
              <div className="space-y-2 opacity-50">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Later</p>
                {['Reply to Mike', 'Review Q4 notes'].map((task, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-muted/20 rounded-lg">
                    <div className="w-4 h-4 rounded-full border border-muted-foreground/30" />
                    <span className="text-sm text-muted-foreground">{task}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-3 bg-primary/10 rounded-lg border border-primary/20">
                <p className="text-sm text-primary font-medium text-center">
                  One priority. Total clarity. Real progress.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
